import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download, Database, BarChart3 } from "lucide-react";
import { DatasetLoader, AVAILABLE_DATASETS, Dataset } from "@/utils/datasetLoader";
import { useToast } from "@/hooks/use-toast";

interface DatasetSelectorProps {
  onDatasetLoaded?: (code: string) => void;
}

export const DatasetSelector = ({ onDatasetLoaded }: DatasetSelectorProps) => {
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleLoadDataset = async () => {
    if (!selectedDataset) return;

    setIsLoading(true);
    try {
      const result = await DatasetLoader.loadDataset(selectedDataset.filename);
      
      if (result.success && result.data) {
        // Generate code to load the dataset
        const datasetCode = `# Dataset: ${selectedDataset.name}
# ${selectedDataset.description}
# Features: ${selectedDataset.features.join(', ')}
# Target: ${selectedDataset.target}
# Type: ${selectedDataset.type}
# Size: ${selectedDataset.size}

import numpy as np
from sklearn.model_selection import train_test_split
from sklearn.naive_bayes import GaussianNB
from sklearn.metrics import accuracy_score, classification_report

# Load ${selectedDataset.name} dataset
print("Loading ${selectedDataset.name} dataset...")
print("Features: ${result.data.feature_names.join(', ')}")
print("Target: ${result.data.target_name}")
print("Samples: ${result.data.X.length}")

# Example: Split data for training
# X_train, X_test, y_train, y_test = train_test_split(X, y, test_size=0.2, random_state=42)

# Example: Train Naive Bayes classifier
# classifier = GaussianNB()
# classifier.fit(X_train, y_train)
# y_pred = classifier.predict(X_test)
# accuracy = accuracy_score(y_test, y_pred)
# print(f"Accuracy: {accuracy:.3f}")

print("Dataset loaded successfully!")
`;

        onDatasetLoaded?.(datasetCode);
        
        toast({
          title: "Dataset Loaded",
          description: `${selectedDataset.name} dataset loaded successfully`,
        });
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to load dataset",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load dataset",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Load Sample Dataset
        </CardTitle>
        <CardDescription>
          Choose from pre-loaded datasets to practice with real data
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Select onValueChange={(value) => {
          const dataset = AVAILABLE_DATASETS.find(d => d.name === value);
          setSelectedDataset(dataset || null);
        }}>
          <SelectTrigger>
            <SelectValue placeholder="Select a dataset" />
          </SelectTrigger>
          <SelectContent>
            {AVAILABLE_DATASETS.map((dataset) => (
              <SelectItem key={dataset.name} value={dataset.name}>
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  {dataset.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedDataset && (
          <Card className="bg-muted/50">
            <CardContent className="pt-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium">{selectedDataset.name}</h4>
                  <Badge variant={selectedDataset.type === 'classification' ? 'default' : 'secondary'}>
                    {selectedDataset.type}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {selectedDataset.description}
                </p>
                <div className="flex flex-wrap gap-1 text-xs">
                  <span className="text-muted-foreground">Features:</span>
                  {selectedDataset.features.slice(0, 3).map((feature) => (
                    <Badge key={feature} variant="outline" className="text-xs">
                      {feature}
                    </Badge>
                  ))}
                  {selectedDataset.features.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{selectedDataset.features.length - 3} more
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>Target: {selectedDataset.target}</span>
                  <span>Size: {selectedDataset.size}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Button 
          onClick={handleLoadDataset} 
          disabled={!selectedDataset || isLoading}
          className="w-full"
        >
          <Download className="mr-2 h-4 w-4" />
          {isLoading ? "Loading..." : "Load Dataset"}
        </Button>
      </CardContent>
    </Card>
  );
};